/*

PW Slider Control

#TODO could use a refactor to handle single values and ranges more simply



Some code adapted from https://github.com/mpowaga/react-slider, and extended to enable dual-handle range slider controls

---

The MIT License (MIT)

Copyright (c) 2014 Michal Powaga

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.

*/

import React from 'react'
import T from 'prop-types'
import ReactDOM from 'react-dom'
import DimensionAware from '../hoc/DimensionAware'
import { partial } from 'lodash'

const AXES = {
  horizontal: 'X',
  vertical: 'Y'
}

const RANGE_BASE_PX = 100 //matches base px length from CSS for scale calculation

const SIZES = {
  horizontal: 'clientWidth',
  vertical: 'clientHeight'
}

const POSITIONS = {
  horizontal: {
    min: 'left',
    max: 'right'
  },
  vertical: {
    min: 'top',
    max: 'bottom'
  }
}

class Slider extends React.Component {
  static displayName = 'Slider'

  static propTypes = {
    orientation: T.oneOf(['horizontal', 'vertical']),
    minValue: T.number,
    maxValue: T.number,
    step: T.number,
    initialRange: T.array,
    onChange: T.func,
    onStop: T.func, //Called on interaction start
    isRange: T.bool,

    height: T.number,
    width: T.number
  }

  static defaultProps = {
    orientation: 'horizontal', //or 'vertical'
    minValue: 0,
    maxValue: 100,
    step: 1,
    initialRange: [0, 10]
  }

  constructor(props) {
    super(props)
    this.state = {
      activeHandle: ''
    }
  }

  UNSAFE_componentWillMount() {
    const props = this.props

    this.dims = {
      upperBound: 0,
      sliderMin: 0,
      sliderMax: 0
    }

    this.values = {
      valueA: props.initialRange[0],
      valueB: props.initialRange[1],
      valRange: props.initialRange,
      offsetA: 0,
      offsetB: 10,
      offsetRange: [0, 10]
    }
  }

  componentDidMount() {
    this._updateDims(this.props.initialRange)
    // if (this.props.initialRange) {
    //   this.updateRange(this.props.initialRange)
    // }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.height !== this.props.height ||
      nextProps.width !== this.props.width
    ) {
      this._updateDims(this.values.valRange || this.props.initialRange)
    }
  }

  updateRange = newRange => {
    // Call this method on the ref to force new values
    // Caller is responsible for ensureing values fall within max/min bounds
    var A, B
    if (this.props.isRange) {
      A = this._getValueAndOffset(newRange[0])
      B = this._getValueAndOffset(newRange[1])
      this.values = {
        valueA: A.nextValue,
        valueB: B.nextValue,
        valRange: [
          Math.min(A.nextValue, B.nextValue),
          Math.max(A.nextValue, B.nextValue)
        ],
        offsetA: A.nextOffset,
        offsetB: B.nextOffset,
        offsetRange: [
          Math.min(A.nextOffset, B.nextOffset),
          Math.max(A.nextOffset, B.nextOffset)
        ]
      }
    } else {
      A = this._getValueAndOffset(newRange[0])
      this.values = {
        valueA: A.nextValue,
        valRange: [A.nextValue],
        offsetA: A.nextOffset,
        offsetRange: [A.nextOffset]
      }
    }
    this.forceUpdate() //Need to force DOM to pick up dimensions based on the new range
  }

  _updateDims = setRange => {
    const slider = ReactDOM.findDOMNode(this)
    const rect = slider.getBoundingClientRect()
    const size = SIZES[this.props.orientation]
    const position = POSITIONS[this.props.orientation]
    this.dims = {
      upperBound: slider[size],
      sliderMin: rect[position.min],
      sliderMax: rect[position.max]
    }
    this.updateRange(setRange)
  }

  _getRangeStyles = () => {
    const axis = AXES[this.props.orientation]
    const offsetRange = this.values.offsetRange
    const trans = `translate${axis}(${offsetRange[0]}px) scale${axis}(${(offsetRange[1] -
      offsetRange[0]) /
      RANGE_BASE_PX})`
    return {
      WebkitTransform: trans,
      transform: trans
    }
  }

  _getHandleStyles = handleId => {
    const trans = `translate${AXES[this.props.orientation]}(${this.values[
      'offset' + handleId
    ]}px)`
    return {
      WebkitTransform: trans,
      transform: trans
    }
  }

  _onSliderClick = e => {
    const { props, values } = this
    const newPos = this._getMousePropValue(e, ['page' + AXES[props.orientation]])
    if (props.isRange) {
      const val = this._getValueFromPosition(newPos)
      const { nextOffset } = this._getValueAndOffset(val)
      const nearestHandle =
        Math.abs(values.offsetA - nextOffset) <=
        Math.abs(values.offsetB - nextOffset)
          ? 'A'
          : 'B'
      this._moveHandle(nearestHandle, newPos)
    } else {
      this._moveHandle('A', newPos)
    }
    if (props.onStop) {
      props.onStop(e, values.valRange)
    }
  }

  _handleStart = (handleId, e) => {
    this.setState({
      activeHandle: handleId
    })
    if (this.latestDragCb) {
      this._removeListenters() //If any already exist, unbind now
    }
    this.latestDragCb = partial(this._dragMove, handleId)
    document.addEventListener('mousemove', this.latestDragCb, false)
    document.addEventListener('touchmove', this.latestDragCb, false)
    document.addEventListener('mouseup', this._onSliderDrop, false)
    document.addEventListener('touchend', this._onSliderDrop, false)
    e.preventDefault()
    e.stopPropagation()
  }

  _getMousePropValue = (e, mouseProp) => {
    return e.type.indexOf('touch') !== -1
      ? e.touches[0][mouseProp]
      : e[mouseProp]
  }

  _dragMove = (handleId, e) => {
    // this._moveHandle(handleId, e['page' + AXES[this.props.orientation]])
    this._moveHandle(
      handleId,
      this._getMousePropValue(e, ['page' + AXES[this.props.orientation]])
    )
    e.preventDefault()
    e.stopPropagation()
  }

  _onSliderDrop = e => {
    this.setState({ activeHandle: '' })
    this._removeListenters()
    const onStop = this.props.onStop
    if (onStop) onStop(e, this.values.valRange)
    e.preventDefault()
    e.stopPropagation()
  }

  _removeListenters = () => {
    document.removeEventListener('mousemove', this.latestDragCb, false)
    document.removeEventListener('touchmove', this.latestDragCb, false)
    document.removeEventListener('mouseup', this._onSliderDrop, false)
    document.removeEventListener('touchend', this._onSliderDrop, false)
    this.latestDragCb = null
  }

  _touchMove = (handleId, e) => {
    const last = e.changedTouches[e.changedTouches.length - 1]
    this._moveHandle(handleId, last['page' + AXES[this.props.orientation]])
    e.preventDefault()
  }

  _moveHandle = (handleId, pos) => {
    const { props, values } = this
    const lastValue = values[`value${handleId}`]
    const value = this._getValueFromPosition(pos)
    const { nextValue, nextOffset } = this._getValueAndOffset(value, handleId)
    values[`value${handleId}`] = nextValue
    values[`offset${handleId}`] = nextOffset
    if (nextValue !== lastValue) {
      if (props.isRange) {
        const otherId = handleId === 'A' ? 'B' : 'A'
        const newValRange = [
          Math.min(values[`value${otherId}`], nextValue),
          Math.max(values[`value${otherId}`], nextValue)
        ]
        const newOffsetRange = [
          Math.min(values[`offset${otherId}`], nextOffset),
          Math.max(values[`offset${otherId}`], nextOffset)
        ]
        values.valRange = newValRange
        values.offsetRange = newOffsetRange
        if (props.onChange) props.onChange(newValRange)
      } else {
        values.valRange = [nextValue]
        values.offsetRange = [nextOffset]
        if (props.onChange) props.onChange(nextValue)
      }
    }
    this.forceUpdate()
  }

  _getValueFromPosition = pos => {
    const { props, dims } = this
    const ratio = (pos - dims.sliderMin) / (dims.sliderMax - dims.sliderMin)
    return ratio * (props.maxValue - props.minValue) + props.minValue
  }

  _getValueAndOffset = (value, handleId) => {
    const { props, dims } = this
    const nextValue = this._trimValue(value, handleId)
    const nextRatio =
      (nextValue - props.minValue) / (props.maxValue - props.minValue)
    const nextOffset = nextRatio * dims.upperBound
    return { nextValue, nextOffset }
  }

  _trimValue = (val, handleId) => {
    const { props, values } = this
    // Do not allow handle to cross one another
    if (val <= props.minValue) val = props.minValue
    if (val >= props.maxValue) val = props.maxValue

    const valModStep = (val - props.minValue) % props.step
    let alignValue = val - valModStep
    if (Math.abs(valModStep) * 2 >= props.step) {
      if (valModStep > 0) {
        alignValue += props.step
      } else {
        alignValue += -props.step
      }
    }
    if (props.isRange) {
      if (handleId === 'A' && alignValue >= values.valueB) {
        alignValue = values.valueB - props.step
      } else if (handleId === 'B' && alignValue <= values.valueA) {
        alignValue = values.valueA + props.step
      }
    }
    return parseFloat(alignValue.toFixed(5))
  }

  _bindRangeRef = el => {
    this._range = el
  }

  _bindHandleARef = el => {
    this._handleA = el
  }

  _bindHandleBRef = el => {
    this._handleB = el
  }

  render() {
    const { props, state, values } = this
    return props.isRange
      ? <div
          className={`pw_slider active_${state.activeHandle} ${props.orientation}`}
          onMouseDown={this._onSliderClick}
          onTouchStart={this._onSliderClick}
        >
          <div
            className="pw_slider_range"
            ref={this._bindRangeRef}
            style={this._getRangeStyles()}
          />
          <div
            className="pw_slider_handle handle_a"
            onMouseDown={partial(this._handleStart, 'A')}
            onTouchStart={partial(this._handleStart, 'A')}
            ref={this._bindHandleARef}
            style={this._getHandleStyles('A')}
          />
          <div
            className="pw_slider_handle handle_b"
            onMouseDown={partial(this._handleStart, 'B')}
            onTouchStart={partial(this._handleStart, 'B')}
            ref={this._bindHandleBRef}
            style={this._getHandleStyles('B')}
          />
          {props.labelA
            ? <div className="slider_label label_a">
                {values.valRange[0]} {props.labelA}
              </div>
            : null}
          {props.labelB
            ? <div className="slider_label label_b">
                {values.valRange[1]} {props.labelB}
              </div>
            : null}
        </div>
      : <div
          className={`pw_slider active_${state.activeHandle} ${props.orientation}`}
          onMouseDown={this._onSliderClick}
          onTouchStart={this._onSliderClick}
        >
          <div
            className="pw_slider_handle handle_a"
            onMouseDown={partial(this._handleStart, 'A')}
            onTouchStart={partial(this._handleStart, 'A')}
            ref={this._bindHandleARef}
            style={this._getHandleStyles('A')}
          />
          {props.labelA
            ? <div className="slider_label label_a">
                {values.valRange[0]} {props.labelA}
              </div>
            : null}
          {props.labelB
            ? <div className="slider_label label_b">
                {values.valRange[1]} {props.labelB}
              </div>
            : null}
        </div>
  }
}
export default DimensionAware(Slider)
